import React from "react";
import styled from "styled-components";

import {
  BlockRemoveButton,
  ReferencePickerPlugin,
  UnstyledAnchor,
} from "@adsk/acc-reference-picker";
import Badge from "@adsk/alloy-react-badge";
import { useLocale } from "@adsk/alloy-react-locale";
import theme from "@adsk/alloy-react-theme";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";

import CategoryName from "../../components/CategoryName";
import {
  SystemWithCategoryAndStatus,
  UNSPECIFIED_STATUS_COLOR_KEY,
} from "../../models";
import getSystemUrl from "../helpers/getSystemUrl";
import { StatusName } from "../shared/DataView/StatusCell";
import InlineStatusColorIndicator from "../shared/InlineStatusColorIndicator";

const Container = styled(UnstyledAnchor)`
  background-color: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.charcoal100};
  cursor: pointer;
  display: flex;
  height: 90px;
  justify-content: space-between;
  align-items: center;
  padding: 14px;

  &:hover .AssetsEntityBlock__remove-button {
    visibility: visible;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  height: 62px;
  justify-content: space-evenly;
  overflow: hidden;
`;

const StyledBadge = styled(Badge)`
  margin-left: 8px;
`;

const Name = styled.div(theme.typography.heading4);

const Status = styled.div({
  ...theme.typography.labelSmall,
  marginTop: "4px",
});

const SystemsEntityBlock: ReferencePickerPlugin<SystemWithCategoryAndStatus>["EntityBlock"] =
  ({ entity: system, projectId, onRemoveClick, onReferenceClickAnalytics }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const statusColor = system.status
      ? system.status.color
      : UNSPECIFIED_STATUS_COLOR_KEY;
    const statusName = system.status
      ? system.status.label
      : T.translate("UNSPECIFIED");

    return (
      <Container
        onClick={onReferenceClickAnalytics}
        href={getSystemUrl(projectId, system.id)}
      >
        <Details>
          <Name>
            <OverflowTooltip content={system.name}>
              {system.name}
              <StyledBadge
                size={Badge.SIZES.SMALL}
                variant={Badge.VARIANTS.OUTLINE}
              >
                {T.translate("SYSTEM")}
              </StyledBadge>
            </OverflowTooltip>
          </Name>
          {system.category && <CategoryName category={system.category} />}
          <Status>
            <InlineStatusColorIndicator color={statusColor} />
            <StatusName isUnspecified={!system.status}>
              <OverflowTooltip
                getTargetProps={() => ({ style: { overflow: "hidden" } })}
              >
                {statusName}
              </OverflowTooltip>
            </StatusName>
          </Status>
        </Details>
        {onRemoveClick && (
          <BlockRemoveButton
            className="AssetsEntityBlock__remove-button"
            style={{ visibility: "hidden" }}
            onClick={onRemoveClick}
          />
        )}
      </Container>
    );
  };

export default SystemsEntityBlock;
